/**
 * Auto-migration utility for shopBeaver localStorage → backend migration
 *
 * Migrates shop configuration from localStorage to the backend API.
 * Runs automatically on first admin visit after deployment.
 *
 * @returns true if migration was performed, false if already migrated or nothing to migrate
 */

const API_BASE = (import.meta.env.VITE_SHOP_API_URL ?? "/api").replace(/\/+$/, "");
const MIGRATION_FLAG = 'wt_shop_migration_done';

export async function migrateLocalStorageToBackend(): Promise<boolean> {
  // Check if migration already completed
  const migrationFlag = localStorage.getItem(MIGRATION_FLAG);
  if (migrationFlag === 'true') {
    console.log('✓ Shop settings migration already completed');
    return false;
  }

  // Extract localStorage data (using actual keys from storefront)
  const orderFormConfig = localStorage.getItem('wt_shop_order_form_config');
  const paymentMethods = localStorage.getItem('wt_shop_payment_methods');

  if (!orderFormConfig && !paymentMethods) {
    console.log('✓ No shop settings to migrate');
    localStorage.setItem(MIGRATION_FLAG, 'true');
    return false;
  }

  const updates: Record<string, unknown> = {};

  // Parse order form configuration
  if (orderFormConfig) {
    try {
      updates.order_form_fields = JSON.parse(orderFormConfig);
      console.log('📋 Found order forms config to migrate');
    } catch (e) {
      console.error('Failed to parse order form config:', e);
    }
  }

  // Parse payment methods configuration
  if (paymentMethods) {
    try {
      updates.payment_methods = JSON.parse(paymentMethods);
      console.log('💳 Found payment methods config to migrate');
    } catch (e) {
      console.error('Failed to parse payment methods:', e);
    }
  }

  // Send to backend
  try {
    console.log('🔄 Migrating shop settings to backend...');

    const res = await fetch(`${API_BASE}/shop/settings`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(updates)
    });

    if (res.ok) {
      console.log('✅ Successfully migrated localStorage to backend');
      console.log(`   - Migrated ${Object.keys(updates).length} setting(s)`);

      // Mark migration as complete
      localStorage.setItem(MIGRATION_FLAG, 'true');

      // Keep localStorage data as backup (don't remove)
      // This allows fallback if backend is temporarily unavailable

      return true;
    } else {
      const errorText = await res.text();
      console.error('❌ Migration failed:', res.status, errorText);
      return false;
    }
  } catch (error) {
    console.error('❌ Migration error:', error);
    return false;
  }
}

/**
 * Reset migration flag (for testing only)
 * This allows the migration to run again
 */
export function resetMigrationFlag(): void {
  localStorage.removeItem(MIGRATION_FLAG);
  console.log('🔄 Migration flag reset');
}
